import type { FC } from 'react';

import { useBooleanFlags } from '@/core/flags/flags.hooks';
import { CurrencyType, Price } from '@/core/tamagoshi/Price/Price';
import { Text } from '@/core/tamagoshi/Text/Text';
import { PRODUCT_PAGE_REVAMP } from '@/domains/productDelivery/constants/FeatureFlags';
import { SuperProSmallLogo } from '@/loyalty/modules/Subscriptions/components/Logo/SuperProSmallLogo';
import { B2B_SUBSCRIPTIONS_MVP_FEATURE_FLAG } from '@/loyalty/modules/Subscriptions/constants';
import { useUserHasSubscription } from '@/loyalty/modules/Subscriptions/hooks/useUserHasSubscription';
import { DeliveryOffer } from '@/productDiscovery/ProductPage/common/modules/Delivery/delivery.ducks';
import { useIsProductEligibleForSubscriptionDiscount } from '@/productDiscovery/ProductPage/common/modules/Delivery/hooks/useIsProductEligibleForSubscriptionDiscount';
import { claimDeliveryFree } from '@/productDiscovery/ProductPage/common/modules/Delivery/translations';
import { getDeliveryWording } from '@/productDiscovery/ProductPage/common/modules/Delivery/Utils/getDeliveryWording';

import styles from './deliveryWording.module.scss';

const SUPER_PRO_SHIPPING_FEE_LIMIT = 200;

interface Props {
  offer: DeliveryOffer;
}

export const DeliveryWordings: FC<Props> = ({ offer }) => {
  const {
    deliveryWordingMode,
    deliveryWording,
    orderBefore,
    orderBeforeWording,
  } = getDeliveryWording(offer);

  const [isSubscriptionsMvpEnabled] = useBooleanFlags([
    B2B_SUBSCRIPTIONS_MVP_FEATURE_FLAG,
  ]);
  const [isProductPageRevampEnabled] = useBooleanFlags([PRODUCT_PAGE_REVAMP]);
  const isUserSubscribed = useUserHasSubscription();
  const isProductEligibleForSubscription =
    useIsProductEligibleForSubscriptionDiscount(offer);

  const shouldDisplaySubscription =
    isSubscriptionsMvpEnabled &&
    isUserSubscribed &&
    isProductEligibleForSubscription;

  const isFreeDelivery = offer.deliveryPrice.amount === 0;
  const price = offer.deliveryPrice.amount;
  const currency = offer.deliveryPrice.currency as CurrencyType;

  return (
    <div className={styles.withVerticalSeparator}>
      <div className={styles.deliveryWordingMode}>
        <Text type="body2" weight="semiBold">
          {deliveryWordingMode}
        </Text>
        {shouldDisplaySubscription ? (
          <div className={styles.deliveryWithSubscription}>
            <Price value={price} currency={currency} obsolete />
            <Text type="body2" weight="semiBold">
              {offer.deliveryPrice.amount <= SUPER_PRO_SHIPPING_FEE_LIMIT ? (
                claimDeliveryFree
              ) : (
                <Price
                  value={price - SUPER_PRO_SHIPPING_FEE_LIMIT}
                  currency={currency}
                />
              )}
            </Text>
            <SuperProSmallLogo className={styles.subscriptionLogoLifecycle} />
          </div>
        ) : isFreeDelivery ? (
          <Text type="body2" weight="semiBold">
            {claimDeliveryFree}
          </Text>
        ) : (
          <Price value={price} currency={currency} />
        )}
      </div>
      <div className={styles.deliveryWordingContainer}>
        <Text type="body2" className={styles.deliveryWordingText}>
          {deliveryWording}
        </Text>
        {!isProductPageRevampEnabled && orderBefore && (
          <Text type="body2" className={styles.deliveryWordingText}>
            {orderBeforeWording}
          </Text>
        )}
      </div>
    </div>
  );
};
